<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { RotateCcw, Search } from '@lucide/vue'
import { Badge, Button, Drawer, EmptyState, Input, Pagination, Section, Select, Spinner, Toolbar } from '@drama/ui'

import { getIntentVideoTaskLogs, listIntentVideoQueue } from '../../api/intent-videos.js'
import { formatDate, formatMs, queueStatusLabel, queueStatusVariant } from './format.js'
import TaskSources from './TaskSources.vue'

const props = defineProps({ scenarioId: { type: String, required: true } })

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const loading = ref(false)
const error = ref('')
const items = ref([])
const nextCursor = ref(null)
const cursors = ref([])
const pageSize = ref(30)
const filters = reactive({ status: '', source: '', q: '', sort: 'updated_at', order: 'desc' })

const statusOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.intentVideo.filters.allStatuses') },
  ...['pending', 'prompting', 'prompt_ready', 'rendering', 'done', 'failed']
    .map(status => ({ value: status, label: queueStatusLabel(t, status) })),
])

const sourceOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.intentVideo.filters.allSources') },
  { value: 'cluster', label: t('visualGameAdmin.intentVideo.source.cluster') },
  { value: 'gameplay', label: t('visualGameAdmin.intentVideo.source.gameplay') },
  { value: 'admin', label: t('visualGameAdmin.intentVideo.source.admin') },
])

const sortOptions = computed(() => [
  { value: 'occurrences', label: t('visualGameAdmin.intentVideo.table.occurrences') },
  { value: 'updated_at', label: t('visualGameAdmin.intentVideo.table.updatedAt') },
  { value: 'created_at', label: t('visualGameAdmin.intentVideo.table.createdAt') },
])

const orderOptions = computed(() => [
  { value: 'desc', label: t('visualGameAdmin.intentVideo.filters.desc') },
  { value: 'asc', label: t('visualGameAdmin.intentVideo.filters.asc') },
])

// 选中的任务/日志同时写进 URL query（?task=&log=），刷新或者把链接发给别人复现问题时
// 能落在同一条日志上，不用重新点两次。
const selectedRequestId = ref(typeof route.query.task === 'string' ? route.query.task : null)
// URL 带着 task 进来的那一次首屏，loadLogs 还没来得及跑，selectedRequestId 已经非空但
// logsData 还是 null——不先把 loading 置真，会落进「非空但 logsData 是 null」的一帧，
// 模板按 logsData.logs.length 判断空态直接崩掉。
const logsLoading = ref(selectedRequestId.value !== null)
const logsError = ref('')
const logsData = ref(null)
const selectedLogId = ref(typeof route.query.log === 'string' ? route.query.log : null)
const sourcesOpen = ref(false)

const summary = computed(() => (nextCursor.value === null
  ? t('visualGameAdmin.intentVideo.pager.loadedAll', { n: items.value.length })
  : t('visualGameAdmin.intentVideo.pager.loadedMore', { n: items.value.length })))

const selectedLog = computed(() => logsData.value?.logs.find(log => log.id === selectedLogId.value) ?? null)

// 老记录里 contextFiles 只存过文件名（字符串数组），新记录才是 {name, url}——
// 两种都认，老的没有 url 就不渲染链接，显示提示文字。
const contextFileLinks = computed(() => {
  const files = selectedLog.value?.request_json?.contextFiles
  if (!Array.isArray(files)) return []
  return files.map(file => (typeof file === 'string' ? { name: file, url: null } : file))
})

onMounted(async () => {
  await loadTasks(null)
  // 任务不在第一页也没关系，日志详情只按 id 查，不依赖它出现在当前这页列表里
  // （左侧列表就只是不会高亮而已，跟翻页翻过去再回来的观感一致）。
  if (selectedRequestId.value) await loadLogs(selectedRequestId.value)
})

async function loadTasks(cursor) {
  loading.value = true
  error.value = ''
  try {
    const data = await listIntentVideoQueue({
      scenario_id: props.scenarioId,
      limit: pageSize.value,
      cursor: cursor ?? undefined,
      status: filters.status || undefined,
      source: filters.source || undefined,
      q: filters.q.trim() || undefined,
      sort: filters.sort,
      order: filters.order,
    })
    items.value = data.items
    nextCursor.value = data.next_cursor
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

function applyFilters() {
  cursors.value = []
  loadTasks(null)
}

function resetFilters() {
  filters.status = ''
  filters.source = ''
  filters.q = ''
  filters.sort = 'updated_at'
  filters.order = 'desc'
  applyFilters()
}

function nextPage() {
  cursors.value.push(nextCursor.value)
  loadTasks(nextCursor.value)
}

function prevPage() {
  cursors.value.pop()
  loadTasks(cursors.value[cursors.value.length - 1] ?? null)
}

function selectTask(row) {
  if (row.id === selectedRequestId.value) return
  selectedRequestId.value = row.id
  selectedLogId.value = null
  sourcesOpen.value = false
  // replace 而不是 push：一路点任务、点日志不该在浏览器历史里堆出一串没用的「后退」。
  router.replace({ query: { ...route.query, task: row.id, log: undefined } })
  loadLogs(row.id)
}

function selectLog(log) {
  selectedLogId.value = log.id
  router.replace({ query: { ...route.query, log: log.id } })
}

async function loadLogs(requestId) {
  logsLoading.value = true
  logsError.value = ''
  logsData.value = null
  try {
    logsData.value = await getIntentVideoTaskLogs(requestId)
  } catch (requestError) {
    logsError.value = requestError.message
  } finally {
    logsLoading.value = false
  }
}

function logTypeLabel(type) {
  return t(`visualGameAdmin.intentVideo.taskLogs.type.${type}`)
}

/** pending/submitting/submitted 是三张源表里各自的「还没到终态」措辞，UI 上统一显示成「进行中」。 */
function logStatusLabel(status) {
  return t(`visualGameAdmin.intentVideo.taskLogs.status.${status}`)
}

function logStatusVariant(status) {
  if (status === 'ok') return 'success'
  if (status === 'error') return 'danger'
  return 'info'
}
</script>

<template>
  <div class="ivl vga-page" data-insp-path="apps/visual-game-admin/src/pages/intent-video/TaskLogsTab.vue#logs">
    <Section>
      <Toolbar>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ t('visualGameAdmin.intentVideo.filters.search') }}</label>
          <Input
            v-model="filters.q"
            type="search"
            size="sm"
            :placeholder="t('visualGameAdmin.intentVideo.filters.queueSearchPlaceholder')"
            @keyup.enter="applyFilters"
          />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.intentVideo.table.status') }}</label>
          <Select v-model="filters.status" :options="statusOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.intentVideo.table.source') }}</label>
          <Select v-model="filters.source" :options="sourceOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.intentVideo.filters.sort') }}</label>
          <Select v-model="filters.sort" :options="sortOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.intentVideo.filters.order') }}</label>
          <Select v-model="filters.order" :options="orderOptions" size="sm" />
        </div>
        <template #actions>
          <Button size="sm" :loading="loading" @click="applyFilters">
            <Search :size="14" /> {{ t('visualGameAdmin.intentVideo.filters.apply') }}
          </Button>
          <Button variant="ghost" size="sm" :disabled="loading" @click="resetFilters">
            <RotateCcw :size="14" /> {{ t('visualGameAdmin.common.reset') }}
          </Button>
        </template>
      </Toolbar>
    </Section>

    <div :class="['ivl__layout', { 'ivl__layout--no-detail': selectedLog === null }]">
      <Section :padded="false" class="ivl__col ivl__col--tasks">
        <div class="ivl__scroll">
          <div v-if="loading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
          <div v-else-if="error" class="vga-state"><p class="vga-error">{{ error }}</p></div>
          <EmptyState
            v-else-if="items.length === 0"
            :title="t('visualGameAdmin.intentVideo.taskLogs.emptyTitle')"
            :description="t('visualGameAdmin.intentVideo.taskLogs.emptyDescription')"
          />
          <ul v-else class="ivl__list">
            <li
              v-for="row in items"
              :key="row.id"
              :class="['ivl__task', { 'is-active': row.id === selectedRequestId }]"
              @click="selectTask(row)"
            >
              <div class="ivl__row-top">
                <Badge :variant="queueStatusVariant(row.status)" size="sm">{{ queueStatusLabel(t, row.status) }}</Badge>
                <span class="ivl__time">{{ formatDate(row.updated_at) }}</span>
              </div>
              <p class="ivl__clamp">{{ row.intent_text }}</p>
              <span class="ivl__log-count">
                {{ t('visualGameAdmin.intentVideo.taskLogs.counts', {
                  logs: row.log_count,
                  turns: row.source_turn_count,
                }) }}
              </span>
            </li>
          </ul>
        </div>
        <Pagination
          class="ivl__pager"
          :has-prev="cursors.length > 0"
          :has-next="nextCursor !== null"
          :loading="loading"
          :summary="summary"
          :prev-label="t('visualGameAdmin.intentVideo.pager.prev')"
          :next-label="t('visualGameAdmin.intentVideo.pager.next')"
          @prev="prevPage"
          @next="nextPage"
        />
      </Section>

      <Section :padded="false" class="ivl__col ivl__col--logs">
        <div v-if="selectedRequestId !== null" class="ivl__col-actions">
          <Button size="sm" variant="secondary" @click="sourcesOpen = true">
            {{ t('visualGameAdmin.intentVideo.taskSources.open') }}
          </Button>
        </div>
        <div class="ivl__scroll">
          <div v-if="selectedRequestId === null" class="vga-state"><p class="vga-muted">{{ t('visualGameAdmin.intentVideo.taskLogs.pickTask') }}</p></div>
          <template v-else>
            <div v-if="logsLoading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
            <div v-else-if="logsError" class="vga-state"><p class="vga-error">{{ logsError }}</p></div>
            <EmptyState
              v-else-if="logsData.logs.length === 0"
              :title="t('visualGameAdmin.intentVideo.taskLogs.noLogs')"
              :description="t('visualGameAdmin.intentVideo.taskLogs.noLogsDescription')"
            />
            <ul v-else class="ivl__list">
            <li
              v-for="log in logsData.logs"
              :key="log.id"
              :class="['ivl__log', { 'is-active': log.id === selectedLogId }]"
              @click="selectLog(log)"
            >
              <div class="ivl__row-top">
                <Badge size="sm">{{ logTypeLabel(log.type) }}</Badge>
                <Badge :variant="logStatusVariant(log.status)" size="sm">{{ logStatusLabel(log.status) }}</Badge>
              </div>
              <p class="ivl__clamp">{{ log.summary }}</p>
              <span class="ivl__time">{{ formatDate(log.created_at) }} · {{ formatMs(log.duration_ms) }}</span>
            </li>
            </ul>
          </template>
        </div>
      </Section>

      <Section v-if="selectedLog !== null" :padded="false" class="ivl__col ivl__col--detail">
        <div class="ivl__scroll">
          <div class="ivl__detail">
            <dl class="ivl__facts">
              <div><dt>{{ t('visualGameAdmin.intentVideo.taskLogs.id') }}</dt><dd class="vga-mono">{{ selectedLog.id }}</dd></div>
              <div v-if="selectedLog.extra?.provider_task_id">
                <dt>{{ t('visualGameAdmin.intentVideo.taskLogs.providerTaskId') }}</dt>
                <dd class="vga-mono">{{ selectedLog.extra.provider_task_id }}</dd>
              </div>
              <div v-if="selectedLog.extra?.poll_count !== undefined">
                <dt>{{ t('visualGameAdmin.intentVideo.taskLogs.pollCount') }}</dt>
                <dd>{{ selectedLog.extra.poll_count }}</dd>
              </div>
            </dl>

            <section v-if="selectedLog.request_json?.full_prompt_url" class="ivl__section">
              <h3>{{ t('visualGameAdmin.intentVideo.taskLogs.fullPrompt') }}</h3>
              <a class="ivl__link" :href="selectedLog.request_json.full_prompt_url" target="_blank" rel="noopener noreferrer">
                {{ selectedLog.request_json.full_prompt_url }}
              </a>
            </section>
            <section v-if="contextFileLinks.length > 0" class="ivl__section">
              <h3>{{ t('visualGameAdmin.intentVideo.taskLogs.contextFiles') }}</h3>
              <ul class="ivl__file-links">
                <li v-for="file in contextFileLinks" :key="file.name">
                  <a v-if="file.url" class="ivl__link" :href="file.url" target="_blank" rel="noopener noreferrer">{{ file.name }}</a>
                  <span v-else class="vga-muted">{{ file.name }}（{{ t('visualGameAdmin.intentVideo.taskLogs.contextFileNoSnapshot') }}）</span>
                </li>
              </ul>
            </section>
            <section v-if="selectedLog.request_json" class="ivl__section">
              <h3>{{ t('visualGameAdmin.intentVideo.taskLogs.request') }}</h3>
              <pre>{{ JSON.stringify(selectedLog.request_json, null, 2) }}</pre>
            </section>
            <section v-if="selectedLog.response_json" class="ivl__section">
              <h3>{{ t('visualGameAdmin.intentVideo.taskLogs.response') }}</h3>
              <pre>{{ JSON.stringify(selectedLog.response_json, null, 2) }}</pre>
            </section>
            <section v-if="selectedLog.extra" class="ivl__section">
              <h3>{{ t('visualGameAdmin.intentVideo.taskLogs.extra') }}</h3>
              <pre>{{ JSON.stringify(selectedLog.extra, null, 2) }}</pre>
            </section>
          </div>
        </div>
      </Section>
    </div>

    <Drawer
      :open="sourcesOpen"
      :title="t('visualGameAdmin.intentVideo.taskSources.open')"
      width="min(760px, 94vw)"
      @update:open="sourcesOpen = $event"
    >
      <TaskSources v-if="sourcesOpen && selectedRequestId !== null" :request-id="selectedRequestId" />
    </Drawer>
  </div>
</template>

<style scoped>
.ivl__layout {
  display: grid;
  grid-template-columns: 300px minmax(360px, 440px) minmax(0, 1fr);
  gap: var(--drama-space-lg);
  align-items: start;
}

.ivl__layout--no-detail {
  grid-template-columns: 320px minmax(0, 1fr);
}

.ivl__col {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 260px);
  min-height: 480px;
}

/* Section 把 slot 内容包了一层 .drama-section__body，这层不参与 .ivl__col 的 flex 布局，
   要显式接上，不然 .ivl__scroll 的 flex:1 够不到 .ivl__col 的高度，整块只能撑开外层页面滚动。 */
.ivl__col :deep(.drama-section__body) {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.ivl__scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.ivl__col-actions {
  display: flex;
  justify-content: flex-end;
  padding: var(--drama-space-sm) var(--drama-space-md);
  border-bottom: 1px solid var(--drama-color-border);
}

.ivl__pager {
  border-top: 1px solid var(--drama-color-border);
  padding: 0 var(--drama-space-md);
}

.ivl__list {
  display: grid;
  gap: 1px;
  margin: 0;
  padding: 0;
  list-style: none;
  background: var(--drama-color-border);
}

.ivl__task, .ivl__log {
  display: grid;
  gap: var(--drama-space-xs);
  padding: var(--drama-space-md);
  background: var(--drama-color-surface);
  cursor: pointer;
}

.ivl__task:hover, .ivl__log:hover {
  background: var(--drama-color-surface-alt);
}

.ivl__task.is-active, .ivl__log.is-active {
  background: color-mix(in srgb, var(--drama-color-primary) 10%, var(--drama-color-surface));
  box-shadow: inset 3px 0 0 var(--drama-color-primary);
}

.ivl__row-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--drama-space-sm);
}

.ivl__time {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
  white-space: nowrap;
}

.ivl__log-count {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.ivl__clamp {
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
  font-size: var(--drama-font-size-sm);
}

.ivl__detail {
  display: grid;
  gap: var(--drama-space-xl);
  padding: var(--drama-space-xl);
}

.ivl__facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: var(--drama-space-md);
  margin: 0;
}

.ivl__facts dt {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.ivl__facts dd {
  margin: 0;
  overflow-wrap: anywhere;
}

.ivl__section {
  display: grid;
  gap: var(--drama-space-md);
}

.ivl__section h3 {
  margin: 0;
  font-size: var(--drama-font-size-md);
}

.ivl__link {
  color: var(--drama-color-primary);
  overflow-wrap: anywhere;
}

.ivl__file-links {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: var(--drama-space-xs);
}

.ivl__section pre {
  max-height: 420px;
  margin: 0;
  padding: var(--drama-space-md);
  overflow: auto;
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  background: var(--drama-color-surface-alt);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-family: var(--vgs-font-mono);
  font-size: var(--drama-font-size-xs);
}

@media (max-width: 1100px) {
  .ivl__layout {
    grid-template-columns: 1fr;
  }

  .ivl__col {
    height: auto;
    max-height: 480px;
  }
}
</style>
